// src/components/EnterpriseConsole.jsx

import React, { useState, useMemo, useCallback } from 'react';
import { BrainCircuit, Database } from 'lucide-react';

import DataIntegrationPanel from './enterprise/DataIntegrationPanel';
import TriangulationPanel from './enterprise/TriangulationPanel';
import DataIntakePanel from './enterprise/DataIntakePanel';
import RecoveryModelPanel from './enterprise/RecoveryModelPanel';
import { CONSOLE_TABS } from './enterprise/consoleTabs';
import AnalystPanel from './enterprise/AnalystPanel';

// Import all our newly created components
import ExecutiveOverview from './enterprise/ExecutiveOverview';
import PredictiveModels from './enterprise/PredictiveModels';
import RoiCalculator from './enterprise/RoiCalculator';
import ReadinessConsole from './enterprise/ReadinessConsole';
import AnonymousFeedback from './enterprise/AnonymousFeedback';

// EDITIONS: the console is a neutral renderer; taxonomy, copy, funnel, ROI
// benchmarks and palette come from the org's edition (organizations/{id}.edition,
// selected in the super-admin Organization editor).
import { useOrganization } from '../context/OrganizationContext';
import DeferredPanel from './enterprise/DeferredPanel';
import { isDeferredTab } from './enterprise/consoleTabs';
import { EditionProvider, useEdition, editionVars } from '../data/editions/EditionContext';
import useEnterpriseData from '../hooks/useEnterpriseData';
import { computeExecutiveMetrics } from '../utils/executiveMetrics';
import { readBusinessInputs, saveBusinessInputs } from '../utils/businessInputs';


const EnterpriseConsoleInner = ({ activeTab = 'cc-overview', onNavigate, currentUser }) => {
    const edition = useEdition();
    const { orgData } = useOrganization();
    // A tab the edition hides (a public program has no leave forecast or ROI)
    // opens the overview instead. So does a tab behind a per-org feature flag
    // (consoleTabs.js `feature`, e.g. the Alberta Recovery Model) that this
    // organization has not switched on.
    const hiddenTabs = edition.hiddenTabs || [];
    const tabDef = CONSOLE_TABS.find((t) => t.id === activeTab);
    const featureOff = !!(tabDef && tabDef.feature && !(orgData?.features && orgData.features[tabDef.feature]));
    const tab = (hiddenTabs.includes(activeTab) || featureOff) ? 'cc-overview' : activeTab;
    // A tab the organization has put on hold (deferredConsoleTabs) keeps its place
    // in the sidebar and opens the holding page instead of the modeled example.
    const deferred = isDeferredTab(orgData, tab);
    // T1.3: one snapshot call per console session; the three data tabs read it
    // and fall back to edition numbers when it isn't live. Copy/taxonomy/palette
    // always come from the edition — only the NUMBERS go live.
    const { data: liveData, refresh: refreshLive } = useEnterpriseData(orgData?.id, undefined, 90);
    // EXECUTIVE METRIC LAYER: ERI (live snapshot) + the org's own business
    // inputs (saved on the org doc, or uploaded) + business averages, computed
    // ONCE here and handed to every tab so nothing contradicts the tab it links
    // to. See docs/EXECUTIVE-METRICS.md.
    const savedInputs = useMemo(() => readBusinessInputs(orgData), [orgData]);
    const metrics = useMemo(() => computeExecutiveMetrics({
        live: liveData, edition, saved: savedInputs, uploaded: liveData?.business,
        attribution: savedInputs.attribution, windowDays: liveData?.windowDays || 90
    }), [liveData, edition, savedInputs]);
    const handleSaveInputs = useCallback((inputs) => saveBusinessInputs(orgData?.id, inputs, currentUser), [orgData?.id, currentUser]);
    // T0.3 badge — flips to "Live data" the moment a real snapshot loads.
    const isLiveData = liveData?.source === 'live';
    const [showDataPanel, setShowDataPanel] = useState(false);

    // The three legs behind every figure, as they stand for THIS organization
    // (the slide-out panel). Nothing here claims a connection that does not exist.
    const legs = useMemo(() => {
        const visibleGroups = (liveData?.groups || []).filter(g => !g.suppressed).length;
        const suppressed = liveData?.suppressedGroups || 0;
        const responded = liveData?.participation?.responded ?? liveData?.responded;
        const uploaded = liveData?.business ? Object.keys(liveData.business).length : 0;
        const typed = Object.keys(savedInputs || {}).filter(k => k !== 'useBenchmark' && k !== 'updatedAt' && k !== 'updatedBy').length;
        const inputs = metrics?.inputs || {};
        const clientCount = Object.values(inputs).filter(v => v && v.source === 'client').length;
        const uploadCount = Object.values(inputs).filter(v => v && v.source === 'upload').length;
        const avgCount = Object.values(inputs).filter(v => v && v.source === 'benchmark').length;
        return [
            {
                id: 'members', kicker: 'Leg 1 · individual, aggregated', name: 'Member check-ins (ERI)',
                status: isLiveData ? 'live' : 'none',
                summary: isLiveData
                    ? `Scored check-ins from the last ${liveData.windowDays || 90} days, each member counted once at their latest answers, rolled up to groups of five or more.`
                    : 'No group has reached five scored members in the window yet, so the console is showing the modeled example. The first five check-ins in a group switch it to live.',
                rows: isLiveData ? [
                    { label: 'Members with a check-in', value: responded ?? '—' },
                    { label: 'Groups shown', value: `${visibleGroups}` },
                    { label: 'Groups suppressed (under five)', value: `${suppressed}` },
                    { label: 'Basis', value: liveData.capacityBasis === 'eri' ? 'ERI: performance, wellbeing, cognitive' : 'Performance mean' }
                ] : [],
                note: 'Individual answers and scores never leave the member. Staff see aggregates only, with complementary suppression so a small group cannot be recovered by subtraction.'
            },
            {
                id: 'organization', kicker: "Leg 2 · the organization's own figures", name: 'Organizational data',
                status: (clientCount + uploadCount) > 0 ? ((clientCount + uploadCount) >= 2 ? 'live' : 'partial') : 'none',
                summary: 'Headcount, loaded labour cost, absence days, turnover and benefits utilization, typed in the ROI tab by an admin or uploaded as CSV into the warehouse. The field guide covers what employers actually hold: hours and overtime, time away, claims, safety, benefits use.',
                rows: [
                    { label: 'Figures the organization typed', value: `${clientCount}` },
                    { label: 'Figures uploaded', value: `${uploadCount}${uploaded ? ` (${uploaded} metric${uploaded === 1 ? '' : 's'} in the warehouse)` : ''}` },
                    { label: 'Figures using the average', value: `${avgCount}` },
                    { label: 'Groups with their own figures', value: `${liveData?.businessByGroup ? Object.keys(liveData.businessByGroup).length : 0}` },
                    ...(typed ? [{ label: 'Last typed', value: savedInputs.updatedAt ? new Date(savedInputs.updatedAt).toLocaleDateString() : '—' }] : [])
                ],
                note: 'Uploads carry their own date and source, and are read by group when the file has one. Today this is a secure CSV; system connections follow a pilot.'
            },
            {
                id: 'averages', kicker: 'Leg 3 · the comparison', name: 'Business averages',
                status: 'builtin',
                summary: "Conservative sector references for absence, turnover, benefits use, claim cost and incidence, with the edition's own assumptions for this sector. Illustrative until a sourced set is on file; used wherever the organization has no figure, and shown beside its figures as the comparison.",
                rows: [
                    { label: 'Sector', value: edition.label },
                    { label: 'Attribution ceiling', value: 'never above 40%' }
                ],
                note: 'Edits in the console\'s "Business averages & modeled assumptions" panel are session-only; the defaults are documented in docs/EXECUTIVE-METRICS.md.'
            }
        ];
    }, [liveData, isLiveData, savedInputs, metrics, edition]);

    const getPageTitle = () => {
        const titles = edition.pageTitles || {};
        // The recovery-model tab keeps one name whatever the edition, unless the edition titles it.
        if (tab === 'cc-recovery') return titles[tab] || 'The Alberta Recovery Model';
        return titles[tab] || titles.default || 'Command Center';
    };

    return (
        <div style={editionVars(edition)} className="min-h-screen bg-slate-50 p-6 md:p-10 font-sans text-slate-800 relative overflow-x-hidden print:bg-white print:p-0">
            <style>{`
                @media print {
                    @page { size: landscape; margin: 10mm; }
                    .no-print { display: none !important; }
                    body { background: white; -webkit-print-color-adjust: exact; color-adjust: exact; }
                }
            `}</style>

            <DataIntegrationPanel 
                isOpen={showDataPanel} 
                onClose={() => setShowDataPanel(false)} 
                legs={legs}
                canUpload
                onUpload={() => { setShowDataPanel(false); if (onNavigate) onNavigate('cc-data'); }}
            />

            <div className="max-w-[1600px] mx-auto space-y-8 pb-12 print:space-y-4">
                
                {/* HEADER SECTION */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-200 pb-6 no-print">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2.5 bg-[var(--brand-deep)] text-white rounded-xl shadow-md">
                                <BrainCircuit size={28} />
                            </div>
                            <h1 className="text-2xl md:text-3xl font-black text-[var(--brand-deep)] tracking-tight">
                                {getPageTitle()}
                            </h1>
                            <span
                                title={isLiveData
                                    ? "Computed from this organization's own submissions."
                                    : 'Illustrative figures based on a modeled workforce. Live organizational data replaces these once assessments are collected.'}
                                className={`self-center text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border cursor-help ${
                                    isLiveData
                                        ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                                        : 'text-amber-700 bg-amber-50 border-amber-200'
                                }`}
                            >
                                {isLiveData ? 'Live data' : 'Modeled example'}
                            </span>
                        </div>
                        <p className="text-slate-500 text-sm font-medium ml-[60px]">
                            {edition.headerSubtitle || 'Executive metrics built from ERI, this organization’s business data and business averages.'}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-4 md:mt-0">
                        <button
                            onClick={() => setShowDataPanel(true)}
                            className="flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 text-[var(--brand-deep)] rounded-xl hover:bg-slate-50 hover:border-[var(--brand-blue)] text-sm font-bold shadow-sm transition-all active:scale-95"
                        >
                            <Database size={18} className="text-[var(--brand-blue)]"/> Data Integration Layer
                        </button>
                    </div>
                </div>

                {/* ROUTER FOR SUB-VIEWS - Now using the cleaned up components */}
                {tab === 'cc-overview' && <ExecutiveOverview onNavigate={onNavigate} live={liveData} metrics={metrics} />}
                {tab === 'cc-recovery' && <RecoveryModelPanel live={liveData} metrics={metrics} onNavigate={onNavigate} />}
                {tab === 'cc-triangulation' && <TriangulationPanel live={liveData} metrics={metrics} onNavigate={onNavigate} />}
                {tab === 'cc-data' && <DataIntakePanel live={liveData} metrics={metrics} onNavigate={onNavigate} onRefresh={refreshLive} currentUser={currentUser} />}
                {tab === 'cc-ai' && <AnalystPanel currentUser={currentUser} />}
                {tab === 'cc-readiness' && <ReadinessConsole live={liveData} metrics={metrics} />}
                {deferred && <DeferredPanel tab={tab} onNavigate={onNavigate} />}
                {tab === 'cc-predictive' && !deferred && <PredictiveModels live={liveData} metrics={metrics} />}
                {tab === 'cc-roi' && !deferred && <RoiCalculator key={edition.id} live={liveData} savedInputs={savedInputs} onSave={handleSaveInputs} />}
                {tab === 'cc-feedback' && <AnonymousFeedback key={edition.id} live={liveData} />}

            </div>
        </div>
    );
};

// Outer shell: resolves the edition from the signed-in (or previewed) org and
// provides it to the whole console subtree. Rendering without an org falls back
// to the default edition, so nothing crashes mid-migration.
const EnterpriseConsole = (props) => {
    const { orgData } = useOrganization();
    return (
        <EditionProvider org={orgData}>
            <EnterpriseConsoleInner {...props} />
        </EditionProvider>
    );
};

export default EnterpriseConsole;